import React, { useRef, useEffect } from 'react';
import moment from 'moment-timezone';

import Appearance from 'styles/Appearance.js';
import Request from 'files/Request.js';

const Utils = {
    addressLookup: async (utils, text, session) => {
        return new Promise(async (resolve, reject) => {
            try {
                let { places } = await Request.get(utils, '/utils/', {
                    type: 'address_lookup',
                    search_text: text,
                    session: session,
                    use_autocomplete: true
                })
                resolve({
                    results: places.map((result, index) => {
                        return {
                            key: index,
                            place_id: result.place_id,
                            name: result.name || 'Name Unavailable',
                            address: result.address
                        }
                    })
                })

            } catch(e) {
                reject(e);
            }
        })
    },
    adjustColor: (color, percent) => {
        var R = parseInt(color.substring(1,3),16);
        var G = parseInt(color.substring(3,5),16);
        var B = parseInt(color.substring(5,7),16);

        R = parseInt(R * (100 + percent) / 100);
        G = parseInt(G * (100 + percent) / 100);
        B = parseInt(B * (100 + percent) / 100);

        R = (R<255)?R:255;
        G = (G<255)?G:255;
        B = (B<255)?B:255;

        var RR = ((R.toString(16).length==1)?"0"+R.toString(16):R.toString(16));
        var GG = ((G.toString(16).length==1)?"0"+G.toString(16):G.toString(16));
        var BB = ((B.toString(16).length==1)?"0"+B.toString(16):B.toString(16));

        return "#"+RR+GG+BB;
    },
    attemptPhoneCall: (phoneNumber, onFallback) => {

        onFallback();
        return;

        // No browser API reports whether a tel: handler exists (mobile dialers,
        // FaceTime/softphones on macOS & desktop all qualify), so we attempt the
        // link and watch for the page to lose focus/visibility as the handler
        // takes over. If nothing claims it within a short window, we invoke the
        // fallback (typically a modal that surfaces the number to dial manually).
        let handled = false;
        let markHandled = () => { handled = true; };
        window.addEventListener('blur', markHandled);
        document.addEventListener('visibilitychange', markHandled);

        window.location.href = `tel:+${('' + phoneNumber).replace(/\D/g, '')}`;

        setTimeout(() => {
            window.removeEventListener('blur', markHandled);
            document.removeEventListener('visibilitychange', markHandled);
            if(!handled && !document.hidden && typeof(onFallback) === 'function') {
                onFallback();
            }
        }, 1200);
    },
    attributeForKey: {
        select: (e, key) => {
            let optionElement = e.target.childNodes[e.target.selectedIndex];
            return optionElement.getAttribute(key);
        }
    },
    convertBytes: (bytes, decimals) => {
        if(isNaN(bytes) || bytes === 0) {
            return '0 Bytes';
        }

        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    },
    downloadVCard: ({ name, org, phone } = {}) => {

        // build a minimal vCard 3.0 so the customer can save the dealer's
        // call-back number to their contacts (keeps the call off the unknown-
        // callers list). phone keeps its human-readable formatting in the value.
        let displayName = name || 'OmniShield Authorized Dealer';
        let lines = [
            'BEGIN:VCARD',
            'VERSION:3.0',
            `FN:${displayName}`
        ];
        if(org) {
            lines.push(`ORG:${org}`);
        }
        if(phone) {
            lines.push(`TEL;TYPE=VOICE:${phone}`);
        }
        lines.push('END:VCARD');

        // trigger a download via a temporary object-url anchor
        let blob = new Blob([lines.join('\r\n')], { type: 'text/vcard;charset=utf-8' });
        let url = URL.createObjectURL(blob);
        let anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `${displayName.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.vcf`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);
    },
    formatAddress: (props = {}) => {

        // return formatted address in accordance with google auto complete formatting
        let { administrative_area_level_1, country, locality, postal_code, street_address_1, street_address_2 } = props || {};
        if(administrative_area_level_1 && country && locality && postal_code && street_address_1) {
            return `${street_address_1}${street_address_2 ? `, ${street_address_2}` : ''}. ${locality}, ${administrative_area_level_1} ${postal_code}, ${country}`;
        }
        // return ala-carte address components as they are found
        return [ street_address_1, street_address_2, locality, administrative_area_level_1, postal_code, country ].filter(val => {
            return val && val.toString().length > 1 ? true : false;
        }).join(', ');
    },
    formatDate: (date, withoutTime = false) => {
        if(!date ) {
            return 'Unknown';
        }
        let next_date = moment(date);
        if(next_date.isValid() !== true) {
            return 'Date is not valid';
        }
        if(moment().isSame(next_date, 'day')) {
            return moment(date).format(withoutTime ? '[Today] MMMM Do' : '[Today at] h:mma');
        }
        if(moment().subtract(1, 'days').isSame(next_date, 'day')) {
            return moment(date).format(withoutTime ? '[Yesterday] MMMM Do' : '[Yesterday at] h:mma');
        }
        if(next_date > moment() && next_date <= moment().add(6, 'days')) {
            return moment(date).format(withoutTime ? 'dddd MMMM Do' : 'dddd [at] h:mma');
        }
        if(moment().isSame(next_date, 'year')) {
            return moment(date).format(withoutTime ? 'MMMM Do' : 'MMM Do [at] h:mma');
        }
        return moment(date).format(withoutTime ? 'MM/DD/YYYY' : 'MM/DD/YYYY [at] h:mma');
    },
    formatLocation: props => {
        const truncate = n => {
            return n > 0 ? Math.floor(n) : Math.ceil(n);
        }
        const getDMS = (dd, longOrLat) => {
            let hemisphere = /^[WE]|(?:lon)/i.test(longOrLat)
            ? dd < 0
              ? "W"
              : "E"
            : dd < 0
              ? "S"
              : "N";

            const absDD = Math.abs(dd);
            const degrees = truncate(absDD);
            const minutes = truncate((absDD - degrees) * 60);
            const seconds = ((absDD - degrees - minutes / 60) * Math.pow(60, 2)).toFixed(2);

            let dmsArray = [degrees, minutes, seconds, hemisphere];
            return `${dmsArray[0]}°${dmsArray[1]}'${dmsArray[2]}" ${dmsArray[3]}`;
        }
        let lat = props.lat || props.latitude;
        let long = props.long || props.longitude;
        return `${getDMS(lat, 'lat')} by ${getDMS(long, 'long')}`
    },
    formatPhoneNumber: phoneNumber => {
        let cleaned = ('' + phoneNumber).replace(/\D/g, '');
        let match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
        if(match) {
            var intlCode = (match[1] ? '+1 ' : '');
            return [intlCode, '(', match[2], ') ', match[3], '-', match[4]].join('');
        }
        return phoneNumber;
    },
    geocode: async (utils, location) => {
        return new Promise(async (resolve, reject) => {
            try {
                let { result } = await Request.get(utils, '/utils/', {
                    type: 'geocode_location',
                    ...location
                })
                resolve({
                    ...result,
                    ...result.location && {
                        location: {
                            latitude: result.location.lat,
                            longitude: result.location.long
                        }
                    }
                });

            } catch(e) {
                reject(e);
            }
        })
    },
    getPhoneNumber: () => {
        return Utils.isCanadianVisitor() ? '1-905-930-5373' : '1-317-834-6599';
    },
    hexToRGBA: (hex, alpha) => {

        var c;
    	if(/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    		c= hex.substring(1).split('');
    		if(c.length === 3){
    			c= [c[0], c[0], c[1], c[1], c[2], c[2]];
    		}
    		c= '0x'+c.join('');
    		return 'rgba('+[(c>>16)&255, (c>>8)&255, c&255].join(',')+',' + alpha + ')';
    	}
    	return hex;
    },
    isCanadianVisitor: () => {

        // if domain name contains omnishieldca, assume it's a Canadian visitor (e.g. from the omnishieldca.homesafenetwork.com marketing campaign)
        if(window.location.hostname.includes('omnishieldca')) {
            return true;
        }

        // device timezone is a strong, network-free proxy for physical
        // location — match it against the IANA zones moment assigns to Canada
        try {
            let timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if(timezone && (moment.tz.zonesForCountry('CA') || []).includes(timezone)) {
                return true;
            }
        } catch(e) {
            // Intl unavailable — fall through to the locale check
        }

        // locale fallback: e.g. en-CA, fr-CA
        let locales = navigator.languages || [navigator.language || ''];
        return locales.some(locale => /-CA$/i.test(locale || ''));
    },
    isMobile: () => {
        return window.innerWidth < 767.98;
    },
    lcFirst: text => {
        return text ? (text.charAt(0).toLowerCase() + text.substring(1)) : '';
    },
    numberFormat: value => {
        if(value > 1000000) {
            return `${(parseFloat(value) / 1000000).toFixed(2)} M`;
        }
        if(value > 1000) {
            return `${(parseFloat(value) / 1000).toFixed(2)} K`;
        }
        return value % 1 !== 0 ? parseFloat(value).toFixed(1) : value;
    },
    parseDuration: duration => {

        let d = parseInt(duration || 0);
        let h = Math.floor(d / 3600);
        let m = Math.floor(d % 3600 / 60);
        let s = Math.floor(d % 3600 % 60);

        var hours = h > 0 ? h + (h === 1 ? ' hour':' hours') : null;
        var minutes = m > 0 ? m + (m === 1 ? ' minute':' minutes') : null;
        if(h >= 24) {
            let _hours = h - 24;
            let days = parseInt(Math.floor(h / 24));
            let string = `${days} ${days === 1 ? 'day':'days'}`;
            if(_hours > 0 && minutes) {
                string += `, ${_hours} ${_hours === 1 ? 'hour':'hours'}, and ${minutes}`;
            } else if(_hours) {
                string += ` and ${_hours} ${_hours === 1 ? 'hour':'hours'}`;
            } else if(minutes) {
                string += ` and ${minutes}`;
            }
            return string;
        } else if(hours && minutes) {
            return hours + ' and ' + minutes;
        } else if(hours) {
            return hours;
        } else if(m > 1) {
            return minutes;
        }
        return s + ' seconds';
    },
    random: {
        string: (length = 5) => {
            let string = '';
            let characters = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ1234567890-_';
            for(let i = 0; i <= length; i++) {
                let n = Math.floor(Math.random() * (characters.length - 1));
                string += characters[n];
            }
            return string;
        }
    },
    sleep: async seconds => {
        return new Promise(resolve => {
            setTimeout(resolve, (seconds * 1000));
        })
    },
    toCurrency: (value, currency) => {
        return parseFloat(value || 0).toLocaleString('en-US', {
            style: 'currency',
            currency: currency || 'USD'
        })
    },
    ucFirst: text => {
        return text ? (text.charAt(0).toUpperCase() + text.substring(1)) : '';
    },
    validate: {
        date: date => {
            return date ? moment(date).isValid() : false;
        },
        email: text => {
            return text ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text) : false;
        },
        phone: text => {
            return text ? /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/im.test(text) : false;
        }
    }
}
export default Utils;
